import { SdkMessageTypes } from '../constants';
import { HostedWebContentSdkService } from '../hosted-web-content-sdk';
import {
  IFilesService,
  FilePreviewParameters,
  CloudStorageFolder,
  IAddCloudStorageFolderResult,
  ViewerActionTypes,
  FileOpenPreference,
  ITelemetryService,
} from '../interfaces';
import { BaseMessageHandler } from './base-message-handler';
import { formulateSdkResponse, onSdkError } from './messageHandlingUtils';

/**
 * Responsible for handling files messages
 */
export class FilesMessageHandler extends BaseMessageHandler {
  constructor(
    protected appSdkHost: HostedWebContentSdkService,
    protected filesService?: IFilesService,
    telemetryService?: ITelemetryService
  ) {
    super(appSdkHost, telemetryService);
  }
  public initHandlers(): void {
    this.registerHandler(
      SdkMessageTypes.files.openFilePreview,
      (_, ...params: unknown[]) => {
        const filePreviewParams: FilePreviewParameters = {
          entityId: params[0] as string,
          title: params[1] as string,
          description: params[2] as string | undefined,
          type: params[3] as string,
          objectUrl: params[4] as string,
          downloadUrl: params[5] as string | undefined,
          webPreviewUrl: params[6] as string | undefined,
          webEditUrl: params[7] as string | undefined,
          baseUrl: params[8] as string | undefined,
          editFile: params[9] as boolean | undefined,
          subEntityId: params[10] as string | undefined,
          viewerAction: params[11] as ViewerActionTypes | undefined,
          fileOpenPreference: params[12] as FileOpenPreference | undefined,
        };
        if (
          !filePreviewParams.entityId ||
          !filePreviewParams.title ||
          !filePreviewParams.type ||
          !filePreviewParams.objectUrl
        )
          return Promise.reject('Invalid File Preview Parameters');
        return this.filesService!.openFilePreview(filePreviewParams);
      },
      {
        isPrivate: true,
      }
    );
    this.registerHandler(
      SdkMessageTypes.files.getCloudStorageFolders,
      (_, channelId: string) => formulateSdkResponse(channelId, this.filesService!.getCloudStorageFolders),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.files.getCloudStorageFolders, error, scenario),
        isPrivate: true,
      }
    );
    this.registerHandler(
      SdkMessageTypes.files.addCloudStorageFolder,
      (_, channelId: string) =>
        formulateSdkResponse(
          channelId,
          this.filesService!.addCloudStorageFolder,
          (result: IAddCloudStorageFolderResult) => [result.isFolderAdded, result.folders],
          true /* spreadFinalResult */
        ),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.files.addCloudStorageFolder, error, scenario),
        isPrivate: true,
      }
    );
    this.registerHandler(
      SdkMessageTypes.files.deleteCloudStorageFolder,
      (_, channelId: string, folderToDelete: CloudStorageFolder) =>
        formulateSdkResponse({ channelId, folderToDelete }, this.filesService!.deleteCloudStorageFolder),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.files.deleteCloudStorageFolder, error, scenario),
        isPrivate: true,
      }
    );
  }

  protected validateDependencies() {
    if (!this.filesService) {
      throw 'No FilesService provided';
    }
  }
}
